import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';

import { StateController } from '../helpers/state/state-management/state-controller';
import { supportedLanguages } from '../helpers/constants';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('language-selector')
export class LanguageSelector extends LitElement {
    static amplitude_callsite: string = 'Language Selector';

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindTranslationState = new StateController(this, translationData);

    @property({ type: String })
    language: string = translationData.language;

    firstUpdated() {
        this.language = translationData.language;
        translationData.onLanguageChanged(() => {
            this.language = translationData.language;
        });
    }

    async selectLanguage(e: Event) {
        const language_before = this.language ?? 'en';
        const next = (e.target as HTMLSelectElement).value;
        if (next === language_before) {
            return;
        }
        this.language = next;
        await translationData.changeLanguage(language_before, next);
    }

    render() {
        const topic_languages = topicData.topic?.translation_languages ?? [];
        const final_languages = supportedLanguages
            .filter((language: { code: string; }) => topic_languages.some(tl => tl.language === language.code))
            .map(language => ({ ...language, icon: topic_languages.find(tl => tl.language === language.code)?.icon }));
        if (!final_languages.some((language: { code: string; }) => language.code === 'en')) {
            final_languages.push({ code: 'en', name: 'English', nativeName: 'English', icon: undefined });
        }
        const currentIcon = final_languages.find(l => l.code === this.language)?.icon;

        return html`
            <label class="pill">
                ${currentIcon ? html`<img class="flag" src="${currentIcon}" alt="" />` : html`<mwc-icon>language</mwc-icon>`}
                <select aria-label="Language" .value="${this.language}" @change="${this.selectLanguage}">
                    ${final_languages.map(language => html`
                        <option value="${language.code}" ?selected="${this.language === language.code}">${language.nativeName}</option>
                    `)}
                </select>
            </label>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: inline-flex;
            }

            .pill {
                display: inline-flex;
                align-items: center;
                gap: 0.25rem;
                height: 28px;
                padding: 0 0.5rem 0 0.4rem;
                border-radius: 14px;
                background: rgba(255, 255, 255, 0.92);
                color: #1f2937;
                font-size: 0.75rem;
                line-height: 1;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
                cursor: pointer;
            }

            .flag {
                height: 14px;
                border-radius: 2px;
            }

            mwc-icon {
                --mdc-icon-size: 16px;
            }

            select {
                border: none;
                background: transparent;
                color: inherit;
                font: inherit;
                padding: 0;
                cursor: pointer;
                outline: none;
                max-width: 8rem;
            }
        `;
    }

}
